/**
 * Select personalizzato (pattern WAI-ARIA "select-only combobox" + listbox).
 *
 * Sostituisce il `<select>` nativo, il cui menu è disegnato dal sistema operativo e quindi
 * diverso su ogni computer. Il focus resta sul pulsante; l'opzione attiva è esposta con
 * `aria-activedescendant`. Il menu è reso in un portale con posizione fissa (non viene tagliato
 * dai dialog scrollabili), si apre sotto il pulsante o sopra se sotto manca spazio, e nasce
 * dall'ancora (transform-origin) con una breve animazione che con `prefers-reduced-motion`
 * diventa una semplice dissolvenza.
 *
 * Tastiera: ↑/↓ (apre e naviga), Home/End, Invio/Spazio (scegli), Esc (chiudi), Tab (chiudi),
 * digitando le prime lettere si salta all'opzione corrispondente.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './Select.module.css';

export interface SelectOption {
  value: string;
  label: string;
}

/** Durata dell'uscita: deve coincidere con `--select-exit` nel CSS. */
const EXIT_MS = 110;
const ALTEZZA_OPZIONE = 34;
const ALTEZZA_MAX = 280;
const MARGINE = 6;

interface Posizione {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  sopra: boolean;
}

export function Select({
  value,
  onChange,
  options,
  placeholder = 'Seleziona…',
  ariaLabel,
  disabled = false,
  testId,
}: {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  ariaLabel: string;
  disabled?: boolean;
  /** Il pulsante ha questo data-testid; le opzioni `${testId}-opzione-${value}`. */
  testId?: string;
}) {
  const listId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [aperto, setAperto] = useState(false);
  // `montato` resta true durante l'animazione di uscita.
  const [montato, setMontato] = useState(false);
  const [attivo, setAttivo] = useState(0);
  const [pos, setPos] = useState<Posizione | null>(null);
  const cerca = useRef({ testo: '', timer: 0 });
  const uscita = useRef(0);

  const selezionato = options.findIndex((o) => o.value === value);
  const etichetta = selezionato >= 0 ? options[selezionato]?.label : undefined;

  const calcolaPosizione = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const altezza = Math.min(options.length * ALTEZZA_OPZIONE + 8, ALTEZZA_MAX);
    const sotto = window.innerHeight - r.bottom - MARGINE;
    const sopraSpazio = r.top - MARGINE;
    const sopra = sotto < altezza && sopraSpazio > sotto;
    setPos(
      sopra
        ? { left: r.left, width: r.width, bottom: window.innerHeight - r.top + 4, sopra }
        : { left: r.left, width: r.width, top: r.bottom + 4, sopra },
    );
  }, [options.length]);

  const apri = useCallback(() => {
    if (disabled) return;
    window.clearTimeout(uscita.current);
    setAttivo(selezionato >= 0 ? selezionato : 0);
    setMontato(true);
    setAperto(true);
  }, [disabled, selezionato]);

  const chiudi = useCallback(() => {
    setAperto(false);
    window.clearTimeout(uscita.current);
    uscita.current = window.setTimeout(() => setMontato(false), EXIT_MS);
  }, []);

  useLayoutEffect(() => {
    if (montato) calcolaPosizione();
  }, [montato, calcolaPosizione]);

  useEffect(() => () => window.clearTimeout(uscita.current), []);

  // Chiusura al click fuori; riposizionamento su resize e su scroll di qualunque antenato.
  useEffect(() => {
    if (!aperto) return;
    const fuori = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !listRef.current?.contains(t)) chiudi();
    };
    document.addEventListener('pointerdown', fuori);
    window.addEventListener('resize', calcolaPosizione);
    window.addEventListener('scroll', calcolaPosizione, true);
    return () => {
      document.removeEventListener('pointerdown', fuori);
      window.removeEventListener('resize', calcolaPosizione);
      window.removeEventListener('scroll', calcolaPosizione, true);
    };
  }, [aperto, chiudi, calcolaPosizione]);

  // Tiene l'opzione attiva visibile durante la navigazione da tastiera.
  useEffect(() => {
    if (!aperto) return;
    listRef.current?.children[attivo]?.scrollIntoView?.({ block: 'nearest' });
  }, [aperto, attivo]);

  function scegli(i: number) {
    const o = options[i];
    if (!o) return;
    if (o.value !== value) onChange(o.value);
    chiudi();
    triggerRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    const ultimo = options.length - 1;
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        e.preventDefault();
        if (!aperto) return apri();
        setAttivo((i) => Math.max(0, Math.min(ultimo, i + (e.key === 'ArrowDown' ? 1 : -1))));
        return;
      }
      case 'Home':
      case 'End':
        if (!aperto) return;
        e.preventDefault();
        setAttivo(e.key === 'Home' ? 0 : ultimo);
        return;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!aperto) return apri();
        return scegli(attivo);
      case 'Escape':
        if (aperto) {
          e.preventDefault();
          e.stopPropagation();
          chiudi();
        }
        return;
      case 'Tab':
        if (aperto) chiudi();
        return;
      default: {
        if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
        const c = cerca.current;
        window.clearTimeout(c.timer);
        c.testo += e.key.toLowerCase();
        c.timer = window.setTimeout(() => (c.testo = ''), 600);
        const da = c.testo.length === 1 ? attivo + 1 : attivo;
        const ordine = [...options.keys()].map((_, k) => (da + k) % options.length);
        const trovato = ordine.find((k) => options[k]?.label.toLowerCase().startsWith(c.testo));
        if (trovato === undefined) return;
        if (!aperto) apri();
        setAttivo(trovato);
      }
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        className={styles.trigger}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={aperto}
        aria-controls={listId}
        aria-activedescendant={aperto ? `${listId}-${attivo}` : undefined}
        disabled={disabled}
        onClick={() => (aperto ? chiudi() : apri())}
        onKeyDown={onKeyDown}
        data-testid={testId}
      >
        <span className={etichetta === undefined ? styles.placeholder : styles.valore}>
          {etichetta ?? placeholder}
        </span>
        <svg
          className={styles.chevron}
          data-aperto={aperto}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          aria-hidden="true"
        >
          <path
            d="M2.5 4.5 6 8l3.5-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {montato &&
        pos &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            className={styles.lista}
            data-stato={aperto ? 'aperto' : 'chiuso'}
            data-sopra={pos.sopra}
            style={{
              left: pos.left,
              width: pos.width,
              top: pos.top,
              bottom: pos.bottom,
              maxHeight: ALTEZZA_MAX,
            }}
          >
            {options.length === 0 && <li className={styles.vuota}>Nessuna opzione</li>}
            {options.map((o, i) => (
              <li
                key={o.value}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === selezionato}
                className={`${styles.opzione} ${i === attivo ? styles.opzioneAttiva : ''}`}
                // Il mousedown non deve togliere il focus al pulsante.
                onMouseDown={(e) => e.preventDefault()}
                onMouseMove={() => i !== attivo && setAttivo(i)}
                onClick={() => scegli(i)}
                data-testid={testId ? `${testId}-opzione-${o.value}` : undefined}
              >
                <span>{o.label}</span>
                {i === selezionato && (
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                    <path
                      d="m3 7.5 2.7 2.7L11 4.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </>
  );
}
